import React, { useState } from "react";
import { ReactComponent as Search } from "../../assets/img/search.svg";
import { ReactComponent as Check } from "../../assets/img/check.svg";
import { ReactComponent as Cross } from "../../assets/img/cross.svg";
import { HandleMonthName } from "../../Utils/HandleMonthName";
import { BASE_URL_IMG, BASE_URL } from "../../Config/api.config";
import useLocalStorage from "../../Hooks/useLocalStorage";
import WorkerInfo from "../Workers/WorkerInfo";
import {
  CircularProgress,
  Box,
  Typography,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TablePagination,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

const AllWorkersData = ({ workers = [], loading = false, refreshWorkers }) => {
  const [selectedWorkerProfile, setSelectedWorkerProfile] = useState(null);
  const [adminData] = useLocalStorage("adminData", "");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleWorkerClick = async (workerId) => {
    try {
      const res = await fetch(
        `${BASE_URL}adm.profile.workers?client_id=${workerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminData.uid}`,
          },
        }
      );

      if (!res.ok) throw new Error("Error fetching worker profile");

      const data = await res.json();
      setSelectedWorkerProfile(data.result);
    } catch (error) {
      console.error("Failed to fetch worker profile:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${HandleMonthName(date.getMonth() + 1).slice(
      0,
      3
    )} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Empty state
  if (!workers || workers.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
        <SearchIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No workers found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters or search criteria
        </Typography>
      </Paper>
    );
  }

  // Worker detail view
  if (selectedWorkerProfile) {
    return (
      <div>
        <WorkerInfo
          profile={selectedWorkerProfile}
          onBack={() => setSelectedWorkerProfile(null)}
        />
      </div>
    );
  }

  // Main table view
  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Worker
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Closed Jobs
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Applied Jobs
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Reported Msg
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Validated
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Joined On
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((worker) => (
                <TableRow
                  key={worker.id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                    cursor: "pointer",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {worker?.profile_image ? (
                        <Avatar
                          src={`${BASE_URL_IMG}${worker.profile_image}`}
                          alt={`${worker.first_name} ${worker.last_name}`}
                          sx={{ width: 40, height: 40 }}
                          onError={(e) => {
                            e.currentTarget.src = require("../../assets/img/user-worker.png");
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "primary.main",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {getInitials(worker.first_name, worker.last_name)}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {worker.first_name} {worker.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {worker.email}
                        </Typography>
                        {worker.country_code_name && (
                          <Chip
                            label={worker.country_code_name}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={worker.active === 1 ? "Active" : "Inactive"}
                      color={worker.active === 1 ? "success" : "default"}
                      size="small"
                      variant={worker.active === 1 ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" align="center">
                      {worker.closed_jobs || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" align="center">
                      {worker.applied_jobs || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" align="center">
                      {worker.reported_msg || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      {worker.validated == 1 ? (
                        <CheckIcon sx={{ color: "success.main" }} />
                      ) : (
                        <CloseIcon sx={{ color: "error.main" }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(worker.date_created)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleWorkerClick(worker.id)}
                      title="View Details"
                      sx={{ color: "primary.main" }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={workers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: 1,
          borderColor: "divider",
        }}
      />
    </Paper>
  );
};

export default AllWorkersData;
